import { Box3, Group, Vector3 } from 'three';

import {
	createLabelSprite,
	indexToLetter,
	ISLAND_SELECTION_GROUP_TYPE,
	removeSelectionGroups
} from './selection-label.helper';
import { ToolResult } from '../../types/tool.types';
import { ToolContext } from '../tool-context.type';

const LABEL_HEIGHT_OFFSET = 12;

type IslandSelectionArgs = {
	islandIds?: Array<string>;
};

export const getIslandSelection = async (
	args: IslandSelectionArgs,
	context: ToolContext
): Promise<ToolResult> => {
	const { threeInstance, publish } = context;
	if (!threeInstance) {
		return { success: false, error: 'Three.js instance not initialized' };
	}

	const allIslands = threeInstance.getIslands();
	if (!allIslands || allIslands.length === 0) {
		return { success: false, error: 'No islands found in the scene' };
	}

	const objectHolder = allIslands[0].parent as Group;
	if (!objectHolder) {
		return { success: false, error: 'Could not access scene object holder' };
	}

	// Filter islands if specific IDs provided
	const { islandIds } = args || {};
	const islands = islandIds && islandIds.length > 0
		? allIslands.filter((g) => islandIds.includes(g.userData?.id as string))
		: allIslands;

	if (islands.length === 0) {
		return { success: true, data: { mapping: {}, message: 'No matching islands found' } };
	}

	// Remove any existing island selection labels (idempotent)
	removeSelectionGroups(objectHolder, [ISLAND_SELECTION_GROUP_TYPE]);

	// Create container group for all labels
	const labelsGroup = new Group();
	labelsGroup.userData = { type: ISLAND_SELECTION_GROUP_TYPE };

	const mapping: Record<string, string> = {};
	const box = new Box3();
	const center = new Vector3();
	let index = 0;

	islands.forEach((islandGroup) => {
		const islandId = islandGroup.userData?.id as string;
		if (!islandId) return;
		index++;
		const letter = indexToLetter(index);

		// Compute world-space bounding box
		box.setFromObject(islandGroup);
		box.getCenter(center);

		// Create and position sprite
		const sprite = createLabelSprite(letter);
		sprite.position.set(center.x, box.max.y + LABEL_HEIGHT_OFFSET, center.z);
		sprite.userData = { id: islandId, type: 'selection-label', label: letter };

		labelsGroup.add(sprite);
		mapping[letter] = islandId;
	});

	objectHolder.add(labelsGroup);

	publish('islands', 'selection-shown', {
		ref: 'island-selection',
		old: null,
		new: mapping
	});

	return { success: true, data: { mapping } };
};
